import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
    Plus, 
    Trash2, 
    Edit3, 
    ArrowLeft,
    Calendar,
    Tag,
    Save,
    X,
    PenTool
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { journalApi } from '../api';
import { cn, formatDate, formatRelativeTime } from '../lib/utils';
import { useLanguage } from '../context/LanguageContext';

export function JournalPage() {
    const { entryId } = useParams();
    const navigate = useNavigate();
    const { t } = useLanguage();
    
    // Dynamic moods with translations
    const MOODS = [
        { id: 'calm', label: t('calm'), emoji: '😌' },
        { id: 'happy', label: t('happy'), emoji: '😊' },
        { id: 'neutral', label: t('neutral'), emoji: '😐' },
        { id: 'anxious', label: t('anxious'), emoji: '😰' },
        { id: 'sad', label: t('sad'), emoji: '😢' }
    ];
    
    const [entries, setEntries] = useState([]);
    const [selectedEntry, setSelectedEntry] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    
    // Form state
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [mood, setMood] = useState('');
    const [tags, setTags] = useState([]);
    const [tagInput, setTagInput] = useState('');

    // Load entries on mount
    useEffect(() => {
        loadEntries();
    }, []);

    // Handle URL entry ID changes
    useEffect(() => {
        if (entryId && entries.length > 0) {
            const entry = entries.find(e => e.id === entryId);
            if (entry) {
                setSelectedEntry(entry);
                setTitle(entry.title || '');
                setContent(entry.content || '');
                setMood(entry.mood || '');
                setTags(entry.tags || []);
                setIsCreating(false);
                setIsEditing(false);
            }
        } else if (!entryId) {
            setSelectedEntry(null);
            if (!isCreating) {
                resetForm();
            }
        }
    }, [entryId, entries]);

    const loadEntries = async () => {
        setIsLoading(true);
        try {
            const data = await journalApi.getEntries();
            setEntries(data || []);
        } catch (error) {
            console.error('Error loading entries:', error);
            setEntries([]);
        } finally {
            setIsLoading(false);
        }
    };

    const resetForm = () => {
        setTitle('');
        setContent('');
        setMood('');
        setTags([]);
        setTagInput('');
        setIsEditing(false);
        setIsCreating(false);
    };

    const handleCreateEntry = async () => {
        if (!title.trim() || !content.trim()) {
            alert(t('fillTitleContent'));
            return;
        }
        
        setIsSaving(true);
        try {
            const result = await journalApi.createEntry(
                title.trim(), 
                content.trim(), 
                mood || null, 
                tags
            );
            await loadEntries();
            resetForm();
            navigate(`/journal/${result.id}`);
        } catch (error) {
            console.error('Error creating entry:', error);
            alert(t('errorSaving'));
        } finally {
            setIsSaving(false);
        }
    };

    const handleUpdateEntry = async () => {
        if (!selectedEntry || !title.trim() || !content.trim()) {
            alert(t('fillTitleContent'));
            return;
        }
        
        setIsSaving(true);
        try {
            await journalApi.updateEntry(selectedEntry.id, {
                title: title.trim(),
                content: content.trim(),
                mood: mood || null,
                tags
            });
            await loadEntries();
            setIsEditing(false);
        } catch (error) {
            console.error('Error updating entry:', error);
            alert(t('errorUpdating'));
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteEntry = async (id) => {
        if (!window.confirm(t('confirmDelete'))) return;
        
        try {
            await journalApi.deleteEntry(id);
            if (selectedEntry?.id === id) {
                navigate('/journal');
                setSelectedEntry(null);
            }
            await loadEntries();
        } catch (error) {
            console.error('Error deleting entry:', error);
        }
    };

    const handleAddTag = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            const newTag = tagInput.trim();
            if (newTag && !tags.includes(newTag)) {
                setTags([...tags, newTag]);
            }
            setTagInput('');
        }
    };

    const removeTag = (tagToRemove) => {
        setTags(tags.filter(t => t !== tagToRemove));
    };

    const startNewEntry = () => {
        setSelectedEntry(null);
        setTitle('');
        setContent('');
        setMood('');
        setTags([]);
        setTagInput('');
        setIsCreating(true);
        setIsEditing(false);
        navigate('/journal');
    };

    const startEditEntry = () => {
        if (selectedEntry) {
            setTitle(selectedEntry.title || '');
            setContent(selectedEntry.content || '');
            setMood(selectedEntry.mood || '');
            setTags(selectedEntry.tags || []);
            setIsEditing(true);
        }
    };

    const cancelEdit = () => {
        if (isCreating) {
            resetForm();
        } else if (selectedEntry) {
            setTitle(selectedEntry.title || '');
            setContent(selectedEntry.content || '');
            setMood(selectedEntry.mood || '');
            setTags(selectedEntry.tags || []);
            setIsEditing(false);
        }
    };

    // Form Component
    const renderForm = () => (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl mx-auto"
        >
            <div className="bg-[#111916] rounded-2xl border border-emerald-800/40 shadow-lg overflow-hidden">
                {/* Header */}
                <div className="p-6 border-b border-emerald-800/40 flex items-center justify-between">
                    <h2 className="font-heading text-xl font-medium text-emerald-100">
                        {isCreating ? t('newNote') : t('edit')}
                    </h2>
                    <button
                        onClick={cancelEdit}
                        data-testid="cancel-edit"
                        className="p-2 hover:bg-emerald-500/10 rounded-lg text-emerald-500"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Form Body */}
                <div className="p-6 space-y-6">
                    {/* Title */}
                    <div>
                        <label className="block text-sm font-medium text-emerald-400 mb-2">
                            {t('title')} *
                        </label>
                        <input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder={t('titlePlaceholder')}
                            data-testid="journal-title-input"
                            className="w-full bg-[#0A0F0D] border border-emerald-800/50 rounded-xl px-4 py-3 text-emerald-100 placeholder:text-emerald-700 outline-none focus:border-emerald-500 transition-colors"
                        />
                    </div>

                    {/* Content */}
                    <div>
                        <label className="block text-sm font-medium text-emerald-400 mb-2">
                            {t('content')} *
                        </label>
                        <textarea
                            value={content}
                            onChange={(e) => setContent(e.target.value)}
                            placeholder={t('contentPlaceholder')}
                            data-testid="journal-content-input"
                            rows={10}
                            className="w-full bg-[#0A0F0D] border border-emerald-800/50 rounded-xl px-4 py-3 text-emerald-200/90 placeholder:text-emerald-700 outline-none focus:border-emerald-500 resize-none leading-relaxed transition-colors"
                        />
                    </div>

                    {/* Mood Selection */}
                    <div>
                        <label className="block text-sm font-medium text-emerald-400 mb-3">
                            {t('howDoYouFeel')}
                        </label>
                        <div className="flex flex-wrap gap-2">
                            {MOODS.map((m) => (
                                <button
                                    key={m.id}
                                    type="button"
                                    onClick={() => setMood(mood === m.id ? '' : m.id)}
                                    data-testid={`mood-${m.id}`}
                                    className={cn(
                                        "flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all",
                                        mood === m.id
                                            ? "bg-emerald-500 text-white shadow-lg"
                                            : "bg-emerald-800/30 text-emerald-400 hover:bg-emerald-800/50"
                                    )}
                                >
                                    <span>{m.emoji}</span>
                                    {m.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Tags */}
                    <div>
                        <label className="block text-sm font-medium text-emerald-400 mb-3">
                            {t('tags')}
                        </label>
                        
                        {/* Current tags */}
                        {tags.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-3">
                                {tags.map((tag) => (
                                    <span
                                        key={tag}
                                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 rounded-full text-sm"
                                    >
                                        <Tag className="w-3 h-3" />
                                        {tag}
                                        <button
                                            type="button"
                                            onClick={() => removeTag(tag)}
                                            className="ml-1 hover:text-emerald-100 transition-colors"
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}
                        
                        {/* Tag input */}
                        <input
                            type="text"
                            value={tagInput}
                            onChange={(e) => setTagInput(e.target.value)}
                            onKeyDown={handleAddTag}
                            placeholder={t('addTagPlaceholder')}
                            data-testid="tag-input"
                            className="w-full px-4 py-2 bg-[#0A0F0D] border border-emerald-800/50 rounded-xl text-sm text-emerald-200 placeholder:text-emerald-700 outline-none focus:border-emerald-500 transition-colors"
                        />
                    </div>
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-emerald-800/40 flex justify-end gap-3">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={cancelEdit}
                        className="rounded-xl border-emerald-700 text-emerald-400 hover:bg-emerald-800/30"
                    >
                        {t('cancel')}
                    </Button>
                    <Button
                        type="button"
                        onClick={isCreating ? handleCreateEntry : handleUpdateEntry}
                        disabled={!title.trim() || !content.trim() || isSaving}
                        data-testid="save-journal-btn"
                        className="bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl disabled:opacity-50"
                    >
                        <Save className="w-4 h-4 mr-2" />
                        {isSaving ? t('saving') : t('save')}
                    </Button>
                </div>
            </div>
        </motion.div>
    );

    // Entry Detail View
    const renderEntryDetail = () => {
        if (!selectedEntry) return null;
        const moodInfo = MOODS.find(m => m.id === selectedEntry.mood);
        
        return (
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="max-w-3xl mx-auto"
            >
                <button
                    onClick={() => {
                        setSelectedEntry(null);
                        navigate('/journal');
                    }}
                    data-testid="back-to-journal"
                    className="inline-flex items-center gap-2 text-emerald-400 hover:text-emerald-300 mb-6 transition-colors"
                >
                    <ArrowLeft className="w-4 h-4" />
                    {t('backToJournal')}
                </button>

                <div className="bg-[#111916] rounded-2xl border border-emerald-800/40 shadow-lg overflow-hidden">
                    {/* Header */}
                    <div className="p-6 border-b border-emerald-800/40">
                        <div className="flex items-start justify-between mb-4">
                            <div className="flex items-center gap-3 text-emerald-500 text-sm">
                                <Calendar className="w-4 h-4" />
                                {formatDate(selectedEntry.created_at)}
                            </div>
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={startEditEntry}
                                    data-testid="edit-entry-btn"
                                    className="text-emerald-400 hover:bg-emerald-500/10"
                                >
                                    <Edit3 className="w-4 h-4 mr-1" />
                                    {t('edit')}
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDeleteEntry(selectedEntry.id)}
                                    data-testid="delete-entry-btn"
                                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>
                        <h1 className="font-heading text-2xl md:text-3xl font-medium text-emerald-50">
                            {selectedEntry.title}
                        </h1>
                    </div>

                    {/* Content */}
                    <div className="p-6">
                        <p className="text-emerald-200/90 leading-relaxed whitespace-pre-wrap">
                            {selectedEntry.content}
                        </p>
                    </div>

                    {/* Footer with mood and tags */}
                    {(moodInfo || selectedEntry.tags?.length > 0) && (
                        <div className="p-6 border-t border-emerald-800/40 flex flex-wrap items-center gap-3">
                            {moodInfo && (
                                <div className={`mood-badge ${selectedEntry.mood}`}>
                                    <span>{moodInfo.emoji}</span>
                                    {moodInfo.label}
                                </div>
                            )}
                            {selectedEntry.tags?.map((tag) => (
                                <span
                                    key={tag}
                                    className="inline-flex items-center gap-1 px-3 py-1 bg-emerald-800/30 text-emerald-400 rounded-full text-sm"
                                >
                                    <Tag className="w-3 h-3" />
                                    {tag}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            </motion.div>
        );
    };

    // Entries List View
    const renderEntriesList = () => (
        <>
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center mb-12"
            >
                <h1 className="font-heading text-3xl md:text-4xl font-medium text-emerald-50 mb-4">
                    {t('yourJournal')}
                </h1>
                <p className="text-lg text-emerald-300/70 max-w-2xl mx-auto mb-8">
                    {t('journalDescription')}
                </p>
                <Button
                    onClick={startNewEntry}
                    data-testid="new-entry-btn"
                    className="bg-emerald-600 hover:bg-emerald-500 text-white rounded-full px-8 py-6 text-lg shadow-lg shadow-emerald-500/30"
                >
                    <Plus className="w-5 h-5 mr-2" />
                    {t('newNote')}
                </Button>
            </motion.div>

            {isLoading ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="bg-emerald-900/20 rounded-2xl h-48 animate-pulse" />
                    ))}
                </div>
            ) : entries.length === 0 ? (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-center py-16"
                >
                    <div className="w-20 h-20 rounded-2xl bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center mx-auto mb-4">
                        <PenTool className="w-10 h-10 text-emerald-500" />
                    </div>
                    <h3 className="font-heading text-xl font-medium text-emerald-100 mb-2">
                        {t('journalEmpty')}
                    </h3>
                    <p className="text-emerald-400/70 mb-6">
                        {t('startWriting')}
                    </p>
                </motion.div>
            ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {entries.map((entry, index) => {
                        const moodInfo = MOODS.find(m => m.id === entry.mood);
                        return (
                            <motion.div
                                key={entry.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.05 }}
                            >
                                <Link
                                    to={`/journal/${entry.id}`}
                                    data-testid={`journal-entry-${entry.id}`}
                                    className="block group"
                                >
                                    <div className="journal-card h-full flex flex-col">
                                        <div className="flex items-start justify-between mb-3">
                                            <span className="text-sm text-emerald-500/70">
                                                {formatRelativeTime(entry.created_at)}
                                            </span>
                                            {moodInfo && (
                                                <span className="text-lg">{moodInfo.emoji}</span>
                                            )}
                                        </div>

                                        <h3 className="font-heading text-lg font-medium text-emerald-100 mb-2 line-clamp-2">
                                            {entry.title}
                                        </h3>

                                        <p className="text-emerald-400/70 text-sm leading-relaxed line-clamp-3 flex-1">
                                            {entry.content}
                                        </p>

                                        {entry.tags?.length > 0 && (
                                            <div className="flex flex-wrap gap-1 mt-4 pt-4 border-t border-emerald-800/40">
                                                {entry.tags.slice(0, 3).map((tag) => (
                                                    <span
                                                        key={tag}
                                                        className="px-2 py-0.5 bg-emerald-800/30 text-emerald-500 rounded text-xs"
                                                    >
                                                        {tag}
                                                    </span>
                                                ))}
                                                {entry.tags.length > 3 && (
                                                    <span className="px-2 py-0.5 text-emerald-600 text-xs">
                                                        +{entry.tags.length - 3}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </Link>
                            </motion.div>
                        );
                    })}
                </div>
            )}
        </>
    );

    return (
        <div className="min-h-[calc(100vh-4rem)] py-12 px-4 md:px-8">
            <div className="max-w-6xl mx-auto">
                {isCreating || isEditing ? (
                    renderForm()
                ) : selectedEntry ? (
                    renderEntryDetail()
                ) : (
                    renderEntriesList()
                )}
            </div>
        </div>
    );
}

export default JournalPage;
